import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { useFinancial } from '../hooks/useFinancial';
import { useAuth } from '../contexts/AuthContext';
import { getConsultasComDadosFinanceiros } from '../services/dataService';
import { ConsultationValueModal } from '../components/financial/ConsultationValueModal';
import { InvoiceModal } from '../components/financial/InvoiceModal';
import { IRReport } from '../components/financial/IRReport';
import { FinancialDashboard } from '../components/financial/FinancialDashboard';
import { TransactionModal } from '../components/financial/TransactionModal';
import { TransactionList } from '../components/financial/TransactionList';
import { CategoryManager } from '../components/financial/CategoryManager';
import { BudgetManager } from '../components/financial/BudgetManager';
import { CashFlowChart } from '../components/financial/CashFlowChart';
import { CategoryChart } from '../components/financial/CategoryChart';
import { FinancialReports } from '../components/financial/FinancialReports';
import { Consulta } from '../services/mockData';
import { Transaction } from '../types/financial';
import { updateConsultationFinancial } from '../services/dataService';
import { formatCurrency } from '../utils/pdfGenerator';
import './Pages.css';
import '../components/financial/Financial.css';

type TabType = 'dashboard' | 'transacoes' | 'categorias' | 'metas' | 'relatorios' | 'consultas';

export const Financial: React.FC = () => {
  const { user } = useAuth();
  const {
    stats,
    loadStats,
    loading,
    transactions,
    categories,
    accounts,
    budgets,
    loadTransactions,
    createNewTransaction,
    updateTransactionData,
    deleteTransactionData,
    loadCategories,
    createNewCategory,
    updateCategoryData,
    deleteCategoryData,
    loadAccounts,
    loadBudgets,
    createNewBudget,
    updateBudgetData,
    deleteBudgetData,
    loadCashFlowData,
    loadCategorySummary,
    loadMonthlyReport,
    getCategorySpent,
    syncConsultations,
  } = useFinancial();

  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [loadingConsultas, setLoadingConsultas] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedConsulta, setSelectedConsulta] = useState<Consulta | null>(null);
  const [modalValueOpen, setModalValueOpen] = useState(false);
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);
  const [showIRReport, setShowIRReport] = useState(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [transactionFilters, setTransactionFilters] = useState<any>({});
  const [cashFlowData, setCashFlowData] = useState<any[]>([]);
  const [categorySummary, setCategorySummary] = useState<any[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [consultaStatusFilter, setConsultaStatusFilter] = useState<'pago' | 'pendente' | null>(null);

  useEffect(() => {
    if (user?.uid) {
      loadStats(selectedMonth, selectedYear, true);
      loadCategories();
      loadAccounts();
      loadBudgets(selectedYear, selectedMonth);
      loadTransactions();
      // Sincronizar consultas como transações na primeira carga
      syncConsultations().catch(console.error);
      if (activeTab === 'dashboard') {
        loadChartsData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, selectedMonth, selectedYear, activeTab]);

  useEffect(() => {
    if (activeTab === 'transacoes' || activeTab === 'consultas') {
      loadTransactions(transactionFilters);
    }
  }, [activeTab, transactionFilters]);

  const loadConsultas = async () => {
    if (!user?.uid) return;

    setLoadingConsultas(true);
    try {
      const startDate = new Date(selectedYear, selectedMonth - 1, 1);
      const endDate = new Date(selectedYear, selectedMonth, 0, 23, 59, 59);
      let consultasData = await getConsultasComDadosFinanceiros(
        user.uid,
        startDate,
        endDate
      );
      
      // Aplicar filtro de status se houver
      if (consultaStatusFilter) {
        consultasData = consultasData.filter((consulta) => {
          if (consultaStatusFilter === 'pago') {
            return consulta.pago === true;
          } else if (consultaStatusFilter === 'pendente') {
            return consulta.pago !== true;
          }
          return true;
        });
      }
      
      setConsultas(consultasData);
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
    } finally {
      setLoadingConsultas(false);
    }
  };

  const loadChartsData = async () => {
    if (!user?.uid) return;

    setLoadingCharts(true);
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().split('T')[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const [cashFlow, categoryReceitas, categoryDespesas] = await Promise.all([
        loadCashFlowData(startDate, endDate),
        loadCategorySummary(startDate, endDate, 'receita'),
        loadCategorySummary(startDate, endDate, 'despesa'),
      ]);

      setCashFlowData(cashFlow);
      setCategorySummary([...categoryReceitas, ...categoryDespesas]);
    } catch (error) {
      console.error('Erro ao carregar dados dos gráficos:', error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const handleEditValue = (consulta: Consulta) => {
    setSelectedConsulta(consulta);
    setModalValueOpen(true);
  };

  const handleSaveValue = async (data: {
    valor?: number;
    pago?: boolean;
    dataPagamento?: string;
  }) => {
    if (!user?.uid || !selectedConsulta) return;

    try {
      await updateConsultationFinancial(
        user.uid,
        selectedConsulta.pacienteId,
        selectedConsulta.id,
        data
      );
      // Sincronizar consultas como transações (especialmente importante quando marca como pago)
      await syncConsultations();
      // Recarregar todos os dados para atualizar os cards
      await loadConsultas();
      await loadTransactions();
      await loadStats(selectedMonth, selectedYear, true);
      setModalValueOpen(false);
      setSelectedConsulta(null);
    } catch (error) {
      console.error('Erro ao salvar valor:', error);
      alert('Erro ao salvar valor da consulta');
    }
  };

  const handleEmitInvoice = (consulta: Consulta) => {
    setSelectedConsulta(consulta);
    setModalInvoiceOpen(true);
  };

  const handleInvoiceSuccess = () => {
    loadConsultas();
  };

  const handleOpenTransactionModal = (transaction?: Transaction) => {
    setEditingTransaction(transaction || null);
    setTransactionModalOpen(true);
  };

  const handleSaveTransaction = async (data: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingTransaction) {
        await updateTransactionData(editingTransaction.id, data);
      } else {
        await createNewTransaction(data);
      }
      setTransactionModalOpen(false);
      setEditingTransaction(null);
    } catch (error: any) {
      throw error;
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    await deleteTransactionData(transactionId);
  };

  const monthNames = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  useEffect(() => {
    if (activeTab === 'consultas') {
      loadConsultas();
    }
  }, [activeTab, selectedMonth, selectedYear, consultaStatusFilter]);

  const handleCardClick = (action: string, filters?: any) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    // Função auxiliar para calcular datas baseado no período
    const getDateRange = (period?: string) => {
      if (period === 'month') {
        const startDate = new Date(currentYear, currentMonth - 1, 1);
        const endDate = new Date(currentYear, currentMonth, 0, 23, 59, 59);
        return {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        };
      } else if (period === 'year') {
        const startDate = new Date(currentYear, 0, 1);
        const endDate = new Date(currentYear, 11, 31, 23, 59, 59);
        return {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        };
      }
      return {};
    };

    switch (action) {
      case 'receita-total':
        setActiveTab('transacoes');
        setTransactionFilters({ type: 'receita' });
        break;

      case 'receita-mes':
        setActiveTab('transacoes');
        setTransactionFilters({
          type: 'receita',
          ...getDateRange('month'),
        });
        break;

      case 'receita-ano':
        setActiveTab('transacoes');
        setTransactionFilters({
          type: 'receita',
          ...getDateRange('year'),
        });
        break;

      case 'despesa-total':
        setActiveTab('transacoes');
        setTransactionFilters({ type: 'despesa' });
        break;

      case 'despesa-mes':
        setActiveTab('transacoes');
        setTransactionFilters({
          type: 'despesa',
          ...getDateRange('month'),
        });
        break;

      case 'despesa-ano':
        setActiveTab('transacoes');
        setTransactionFilters({
          type: 'despesa',
          ...getDateRange('year'),
        });
        break;

      case 'consultas-pagas':
        setActiveTab('consultas');
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        setConsultaStatusFilter('pago');
        break;

      case 'consultas-pendentes':
        setActiveTab('consultas');
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        setConsultaStatusFilter('pendente');
        break;

      case 'saldo-total':
        setActiveTab('transacoes');
        setTransactionFilters({});
        break;

      case 'saldo-mes':
        setActiveTab('transacoes');
        setTransactionFilters(getDateRange('month'));
        break;

      case 'saldo-ano':
        setActiveTab('transacoes');
        setTransactionFilters(getDateRange('year'));
        break;

      case 'transacoes-total':
        setActiveTab('transacoes');
        setTransactionFilters({});
        break;

      default:
        break;
    }
  };

  const tabs = [
    { id: 'dashboard' as TabType, label: 'Dashboard' },
    { id: 'transacoes' as TabType, label: 'Transações' },
    { id: 'consultas' as TabType, label: 'Consultas' },
    { id: 'categorias' as TabType, label: 'Categorias' },
    { id: 'metas' as TabType, label: 'Metas' },
    { id: 'relatorios' as TabType, label: 'Relatórios' },
  ];

  return (
    <div className="ns-page ns-page-financial">
      <div className="ns-page-header">
        <div>
          <h2>Financeiro</h2>
          <p>
            Controle financeiro completo: receitas, despesas, consultas, cursos e muito mais
          </p>
        </div>
        <div className="ns-page-header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowIRReport(!showIRReport)}
          >
            {showIRReport ? 'Ocultar' : 'Ver'} Relatório IR
          </Button>
        </div>
      </div>

      {showIRReport && (
        <div style={{ marginBottom: '32px' }}>
          <IRReport />
        </div>
      )}

      <div className="ns-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`ns-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="ns-tab-content">
        {activeTab === 'dashboard' && (
          <div>
            <FinancialDashboard 
              stats={stats} 
              loading={loading} 
              showExpanded={true}
              onCardClick={handleCardClick}
            />
            
            {cashFlowData.length > 0 && (
              <div style={{ marginTop: '32px' }}>
                <CashFlowChart data={cashFlowData} loading={loadingCharts} />
              </div>
            )}

            {categorySummary.length > 0 && (
              <div style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                <CategoryChart
                  data={categorySummary.filter(c => c.type === 'receita')}
                  type="receita"
                  loading={loadingCharts}
                />
                <CategoryChart
                  data={categorySummary.filter(c => c.type === 'despesa')}
                  type="despesa"
                  loading={loadingCharts}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'transacoes' && (
          <div>
            {(transactionFilters.type || transactionFilters.startDate) && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#e0f2fe', 
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  Filtros ativos:{' '}
                  {transactionFilters.type && (
                    <strong>{transactionFilters.type === 'receita' ? 'Receitas' : 'Despesas'}</strong>
                  )}
                  {transactionFilters.startDate && transactionFilters.endDate && (
                    <span>
                      {' '}de {new Date(transactionFilters.startDate).toLocaleDateString('pt-BR')} até {new Date(transactionFilters.endDate).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => setTransactionFilters({})}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                >
                  Limpar filtros
                </Button>
              </div>
            )}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Transações Financeiras</h3>
              <Button onClick={() => handleOpenTransactionModal()}>
                Nova Transação
              </Button>
            </div>
            <TransactionList
              transactions={transactions}
              loading={loading}
              onEdit={handleOpenTransactionModal}
              onDelete={handleDeleteTransaction}
              filters={transactionFilters}
              onFilterChange={setTransactionFilters}
            />
          </div>
        )}

        {activeTab === 'consultas' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              {consultaStatusFilter && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#e0f2fe', 
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    Mostrando apenas consultas <strong>{consultaStatusFilter === 'pago' ? 'pagas' : 'pendentes'}</strong>
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setConsultaStatusFilter(null)}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Limpar filtro
                  </Button>
                </div>
              )}
              <div className="ns-financial-filters">
                <select
                  className="ns-input"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{ width: 'auto', padding: '8px' }}
                >
                  {monthNames.map((month, index) => (
                    <option key={index + 1} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  className="ns-input"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{ width: 'auto', padding: '8px' }}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ns-table-wrapper">
              <table className="ns-table ns-financial-consultations-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Paciente</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingConsultas ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                        Carregando consultas...
                      </td>
                    </tr>
                  ) : consultas.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                        Nenhuma consulta com valor registrado para este período.
                      </td>
                    </tr>
                  ) : (
                    consultas.map((consulta) => (
                      <tr key={consulta.id}>
                        <td>
                          {new Date(consulta.dataIso).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </td>
                        <td>{consulta.pacienteNome}</td>
                        <td>
                          {consulta.tipo === 'primeira' ? 'Primeira' : 'Retorno'}
                        </td>
                        <td>
                          {consulta.valor
                            ? formatCurrency(consulta.valor)
                            : '—'}
                        </td>
                        <td>
                          {consulta.pago ? (
                            <span className="ns-financial-status-badge pago">
                              Pago
                            </span>
                          ) : (
                            <span className="ns-financial-status-badge pendente">
                              Pendente
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="ns-financial-actions">
                            <Button
                              variant="ghost"
                              onClick={() => handleEditValue(consulta)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              Editar
                            </Button>
                            {consulta.valor && (
                              <Button
                                variant="ghost"
                                onClick={() => handleEmitInvoice(consulta)}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                Emitir Nota
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'categorias' && (
          <CategoryManager
            categories={categories}
            loading={loading}
            onCreate={createNewCategory}
            onUpdate={updateCategoryData}
            onDelete={deleteCategoryData}
          />
        )}

        {activeTab === 'metas' && (
          <BudgetManager
            budgets={budgets}
            categories={categories}
            loading={loading}
            onCreate={createNewBudget}
            onUpdate={updateBudgetData}
            onDelete={deleteBudgetData}
            onGetCategorySpent={getCategorySpent}
          />
        )}

        {activeTab === 'relatorios' && (
          <FinancialReports
            onGenerateReport={loadMonthlyReport}
            loading={loading}
          />
        )}
      </div>

      <ConsultationValueModal
        isOpen={modalValueOpen}
        onClose={() => {
          setModalValueOpen(false);
          setSelectedConsulta(null);
        }}
        consulta={selectedConsulta}
        onSave={handleSaveValue}
      />

      <InvoiceModal
        isOpen={modalInvoiceOpen}
        onClose={() => {
          setModalInvoiceOpen(false);
          setSelectedConsulta(null);
        }}
        consulta={selectedConsulta}
        onSuccess={handleInvoiceSuccess}
      />

      <TransactionModal
        isOpen={transactionModalOpen}
        onClose={() => {
          setTransactionModalOpen(false);
          setEditingTransaction(null);
        }}
        transaction={editingTransaction}
        categories={categories}
        accounts={accounts}
        onSave={handleSaveTransaction}
      />
    </div>
  );
};
